import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Archive, ArchiveRestore, Loader2, Send, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatDateTime, telHref } from "@/lib/api";
import { STRUCTURE_LABEL } from "@/lib/content";
import { Thread } from "@/pages/Conversation";
import { AdminTitle, StatusBadge, StatusSelect } from "./ui";

export function MessagesList() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const [structure, setStructure] = useState("");
  const [archived, setArchived] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams({ archived, ...(status && { status }), ...(structure && { structure }) });
    api.get(`/admin/conversations?${q}`).then((r) => setRows(r.data));
  }, [status, structure, archived]);
  return (
    <div data-testid="admin-messages">
      <AdminTitle title="Messages & conversations">
        <select className="input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="messages-filter-status">
          <option value="">Tous statuts</option><option value="nouveau">Nouveau</option><option value="en_cours">En cours</option><option value="traite">Traité</option>
        </select>
        <select className="input !w-auto" value={structure} onChange={(e) => setStructure(e.target.value)} data-testid="messages-filter-structure">
          <option value="">Toutes structures</option><option value="ong">ONG</option><option value="association">Association</option><option value="les_deux">Les deux</option>
        </select>
        <button className="btn-outline !py-2 text-u" onClick={() => setArchived(!archived)} data-testid="messages-toggle-archived">
          {archived ? "Voir actifs" : "Voir archives"}
        </button>
      </AdminTitle>
      <div className="card divide-y">
        {rows && !rows.length && <p className="p-6 text-neutral-500" data-testid="messages-empty">Aucun message.</p>}
        {(rows || []).map((c) => (
          <Link key={c.id} to={`/admin/messages/${c.id}`} className="flex flex-col gap-1 p-4 hover:bg-u-tint sm:flex-row sm:items-center sm:gap-4" data-testid={`message-row-${c.id}`}>
            <div className="flex items-center gap-2 sm:w-56">
              {c.unread && <span className="h-2.5 w-2.5 rounded-full bg-asso" aria-label="Non lu" />}
              <span className={`truncate ${c.unread ? "font-bold" : "font-medium"}`}>{c.name}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">{c.subject_label} · <span className="text-neutral-500">{STRUCTURE_LABEL[c.structure]}</span></p>
              <p className="truncate text-sm text-neutral-600">{c.last_message}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              <StatusBadge status={c.status} /> {c.count} msg · {formatDateTime(c.updated_at)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function MessageDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get(`/admin/conversations/${id}`).then((r) => setC(r.data)); }, [id]);
  const patch = async (upd) => {
    await api.patch(`/admin/conversations/${id}`, upd);
    setC((s) => ({ ...s, ...upd }));
    toast.success("Mis à jour");
  };
  const reply = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/conversations/${id}/reply`, { body });
      setC(data);
      setBody("");
      toast.success("Réponse envoyée par e-mail au visiteur");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  if (!c) return <p>Chargement…</p>;
  return (
    <div className="max-w-4xl" data-testid="admin-message-detail">
      <Link to="/admin/messages" className="mb-4 inline-flex items-center gap-1 text-sm text-u" data-testid="message-back"><ArrowLeft className="h-4 w-4" /> Retour</Link>
      <AdminTitle title={c.subject_label}>
        <StatusSelect value={c.status} onChange={(v) => patch({ status: v })} testid="message-status-select" />
        <button className="btn-outline !py-2 text-u" onClick={() => patch({ archived: !c.archived })} data-testid="message-archive-toggle">
          {c.archived ? <><ArchiveRestore className="h-4 w-4" /> Désarchiver</> : <><Archive className="h-4 w-4" /> Archiver</>}
        </button>
      </AdminTitle>
      <div className="card mb-6 grid gap-2 p-5 text-sm sm:grid-cols-2">
        <p><strong>{c.name}</strong> · {c.country}</p>
        <p>Structure : {STRUCTURE_LABEL[c.structure]}</p>
        <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-u"><Mail className="h-4 w-4" /> {c.email}</a>
        {c.phone && <a href={telHref(c.phone)} className="flex items-center gap-1 text-u"><Phone className="h-4 w-4" /> {c.phone}</a>}
      </div>
      <div className="card pagne p-5"><Thread messages={c.messages} mine="admin" /></div>
      <form onSubmit={reply} className="card mt-6 p-5" data-testid="admin-reply-form">
        <label htmlFor="admin-reply" className="label">Répondre (envoyé par e-mail avec un lien sécurisé vers la conversation)</label>
        <textarea id="admin-reply" rows={5} className="input" required value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-reply-input" />
        <button className="btn-u mt-4" disabled={loading} data-testid="admin-reply-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la réponse
        </button>
      </form>
    </div>
  );
}
